import { LIVENESS_POSES, type LivenessPose, type Twin } from '@trendator/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api';
import { ArrowSide, BackIcon, TickIcon } from '../components/icons';
import { useBackButton } from '../hooks';
import { haptic, hapticResult } from '../telegram';

const STEP: Record<LivenessPose, { title: string; hint: string }> = {
  front: { title: 'Посмотри прямо в камеру', hint: 'Лицо целиком в круге, хороший свет' },
  left: { title: 'Медленно поверни голову влево', hint: 'Так мы проверим, что это живой ты, а не чужое фото' },
  right: { title: 'Теперь поверни голову вправо', hint: 'Ещё чуть-чуть — и двойник готов' },
};

/** Снимок с видео → JPEG. Камера фронтальная и зеркальная — снимок не зеркалим (как видит камера). */
async function capture(video: HTMLVideoElement): Promise<Blob | null> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, 1280 / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
}

/** Создаём двойника: проверка живости — docs/design/TwinSetup.dc.html. Снимки не храним. */
export function TwinSetup({ onBack, onDone }: { onBack: () => void; onDone: (twin: Twin) => void }) {
  useBackButton(onBack);
  const [twin, setTwin] = useState<Twin | null>(null);
  const [pose, setPose] = useState<LivenessPose>('front');
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [camera, setCamera] = useState<'starting' | 'on' | 'off'>('starting');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .twin()
      .then((t) => {
        setTwin(t);
        const next = LIVENESS_POSES.find((p) => !t.liveness[p]);
        if (next) setPose(next);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let stopped = false;
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('no camera');
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 } }, audio: false });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCamera('on');
      } catch {
        // Нет доступа к камере в WebView — снимем системной камерой через выбор файла.
        setCamera('off');
      }
    };
    void start();
    return () => {
      stopped = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const send = useCallback(
    async (frame: Blob | null) => {
      if (!frame) return;
      setBusy(true);
      setHint(null);
      try {
        const r = await api.liveness(pose, frame);
        setTwin(r.twin);
        if (!r.ok) {
          hapticResult('error');
          setHint(r.hint);
          return;
        }
        hapticResult('success');
        if (r.twin.status === 'ready') {
          onDone(r.twin);
          return;
        }
        const next = LIVENESS_POSES.find((p) => !r.twin.liveness[p]);
        if (next) setPose(next);
      } catch (err) {
        setHint(err instanceof ApiError && err.code === 'too_many_attempts' ? 'Слишком много попыток — передохни и попробуй позже' : 'Не получилось проверить — попробуй ещё раз');
      } finally {
        setBusy(false);
      }
    },
    [pose, onDone],
  );

  const shoot = async () => {
    haptic();
    if (camera === 'on' && videoRef.current) await send(await capture(videoRef.current));
    else fileRef.current?.click();
  };

  const doneCount = twin ? LIVENESS_POSES.filter((p) => twin.liveness[p]).length : 0;
  const stepIndex = LIVENESS_POSES.indexOf(pose);
  const pct = Math.round(((doneCount + (busy ? 0.5 : 0)) / 3) * 100);

  return (
    <main className="screen twin-setup">
      <header className="twin-setup__header">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        <div className="twin-setup__head">
          <span className="twin-setup__title">Создаём двойника</span>
          <div className="twin-setup__bar">
            {LIVENESS_POSES.map((p) => (
              <span key={p} className={twin?.liveness[p] || p === pose ? 'on' : ''} />
            ))}
          </div>
        </div>
        <span className="twin-setup__step">{stepIndex + 1} из 3</span>
      </header>

      <div className="twin-setup__camera-wrap">
        <div className="twin-setup__ring" style={{ background: `conic-gradient(var(--accent) 0 ${pct}%, rgba(255,255,255,0.12) ${pct}% 100%)` }}>
          <div className="twin-setup__camera">
            <video ref={videoRef} playsInline muted className={camera === 'on' ? '' : 'hidden'} />
            {camera !== 'on' ? <span className="twin-setup__nocam">{camera === 'starting' ? 'Включаем камеру…' : 'Нажми «Снять» — откроется камера'}</span> : null}
            <svg viewBox="0 0 100 100" className="twin-setup__oval" aria-hidden="true">
              <ellipse cx="50" cy="45" rx="23" ry="30" fill="none" stroke="#FFFFFF" strokeOpacity="0.75" strokeWidth="0.8" strokeDasharray="3 3" />
            </svg>
          </div>
          {pose !== 'front' ? (
            <span className={`twin-setup__arrow twin-setup__arrow--${pose}`}>
              <ArrowSide dir={pose} />
            </span>
          ) : null}
        </div>
      </div>

      <div className="twin-setup__text">
        <h1>{STEP[pose].title}</h1>
        <p>{hint ?? STEP[pose].hint}</p>
      </div>

      <div className="glass-list twin-setup__checks">
        <div className="twin-setup__check">
          <span className="twin-setup__ok">
            <TickIcon />
          </span>
          <span className="twin-setup__check-label">{twin?.okPhotos ?? 0} фото загружено</span>
          <span className="twin-setup__check-meta">разные ракурсы</span>
        </div>
        <div className="twin-setup__sep" />
        <div className="twin-setup__check">
          <span className="twin-setup__ok">
            <TickIcon />
          </span>
          <span className="twin-setup__check-label">На фото один и тот же человек</span>
          <span className="twin-setup__check-meta twin-setup__check-meta--ok">{twin?.consistency !== null && twin ? `${twin.consistency}%` : ''}</span>
        </div>
        <div className="twin-setup__sep" />
        <div className="twin-setup__check">
          {doneCount === 3 ? (
            <span className="twin-setup__ok">
              <TickIcon />
            </span>
          ) : (
            <span className="twin-setup__spin" />
          )}
          <span className="twin-setup__check-label">Проверка, что это ты</span>
          <span className="twin-setup__check-meta twin-setup__check-meta--accent">{doneCount} из 3</span>
        </div>
      </div>

      <p className="twin-setup__privacy">Снимки с камеры не сохраняем. Фото двойника зашифрованы и видны только тебе.</p>

      <div className="result__footer">
        <button type="button" className="btn-accent" disabled={busy || !twin} onClick={() => void shoot()}>
          {busy ? 'Проверяем…' : 'Снять'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => void send(e.target.files?.[0] ?? null).finally(() => { if (fileRef.current) fileRef.current.value = ''; })} />
    </main>
  );
}
